import React, { useEffect } from 'react';
import { cn } from '../lib/utils';
import { Button } from './Button';

/**
 * Dialog — modal overlay with backdrop blur.
 * Backed by a simple useState for open/close.
 * Closes on Escape or backdrop click.
 */
export function Dialog({ open, onClose, title, description, children, footer, size = 'md' }) {
  // Close on Escape
  useEffect(() => {
    if (!open) return;
    function onKey(e) {
      if (e.key === 'Escape') onClose?.();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Lock body scroll while open
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [open]);

  if (!open) return null;
  const sizes = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-2xl',
  };
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fade-in"
      onClick={onClose}
      role="presentation"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-bg/80 backdrop-blur-sm" />

      {/* Card — fixed max-height, scrolls if content overflows */}
      <div
        className={cn(
          'relative w-full bg-surface border border-border-strong rounded-xl shadow-lg overflow-hidden animate-slide-up',
          'max-h-[90vh] flex flex-col',
          sizes[size]
        )}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        {(title || description) && (
          <div className="px-6 pt-6 pb-2 shrink-0">
            {title && <h2 className="text-lg font-semibold text-fg">{title}</h2>}
            {description && (
              <p className="text-sm text-fg-muted mt-1">{description}</p>
            )}
          </div>
        )}
        <div className="px-6 py-4 overflow-y-auto flex-1">{children}</div>
        {footer && (
          <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-border bg-surface-2/30 shrink-0">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Skeleton — animated placeholder for loading states.
 */
export function Skeleton({ className, ...props }) {
  return (
    <div
      className={cn(
        'skeleton rounded-md h-4 w-full',
        className
      )}
      {...props}
    />
  );
}

/**
 * EmptyState — illustrated empty container with optional action.
 */
export function EmptyState({ icon: Icon, title, description, action, className }) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center text-center p-8 rounded-lg',
        className
      )}
    >
      {Icon && (
        <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-lg bg-surface-2 border border-border">
          <Icon className="h-5 w-5 text-fg-muted" />
        </div>
      )}
      <h3 className="text-sm font-semibold text-fg">{title}</h3>
      {description && (
        <p className="text-xs text-fg-muted mt-1 max-w-sm">{description}</p>
      )}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

/**
 * Spinner — small loading indicator.
 */
export function Spinner({ className, size = 16 }) {
  return (
    <svg
      className={cn('animate-spin text-fg-muted', className)}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.2" strokeWidth="3" />
      <path
        d="M22 12a10 10 0 0 0-10-10"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
      />
    </svg>
  );
}